import { useState } from 'react';
import { FileClock } from 'lucide-react';
import {
  AUDIT_ACTION_LABELS,
  AUDIT_ACTION_VALUES,
  type AuditLogEntry,
} from '@electrodavisan/shared';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Pagination,
  Select,
  SkeletonList,
  Table,
  type Column,
} from '../../components/ui';
import { formatDateTime } from '../../lib/format';
import { useAuditLog } from '../../services/audit.service';

const PAGE_SIZE = 25;

export function AdminAuditPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');

  const { data, isLoading, isError, refetch } = useAuditLog({
    page,
    pageSize: PAGE_SIZE,
    action: action || undefined,
    entityType: entityType || undefined,
  });

  const columns: Array<Column<AuditLogEntry>> = [
    {
      key: 'date',
      header: 'Fecha',
      render: (entry) => (
        <span className="whitespace-nowrap text-[13px]">{formatDateTime(entry.createdAt)}</span>
      ),
    },
    {
      key: 'action',
      header: 'Accion',
      render: (entry) => (
        <Badge tone="primary">{AUDIT_ACTION_LABELS[entry.action] ?? entry.action}</Badge>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (entry) => (
        <div className="min-w-0 max-w-[14rem]">
          <p className="truncate text-[13px] text-text-primary">{entry.actorEmail ?? '-'}</p>
          <p className="truncate font-mono text-[12px] text-text-muted">{entry.actorSub ?? '-'}</p>
        </div>
      ),
    },
    {
      key: 'entity',
      header: 'Entidad',
      hideBelow: 'md',
      render: (entry) => (
        <div className="min-w-0 max-w-[14rem]">
          <p className="text-[13px] text-text-primary">{entry.entityType}</p>
          <p className="truncate font-mono text-[12px] text-text-muted">{entry.entityId ?? '-'}</p>
        </div>
      ),
    },
    {
      key: 'metadata',
      header: 'Detalle',
      render: (entry) => (
        <details className="max-w-sm text-[12px]">
          <summary className="cursor-pointer">Ver resultado y correlación</summary>
          <code className="block whitespace-pre-wrap break-all font-mono text-text-muted">
            {entry.metadata && Object.keys(entry.metadata).length > 0
              ? JSON.stringify(entry.metadata, null, 2)
              : '-'}
          </code>
        </details>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Auditoria"
        description="Registro append-only de las acciones relevantes del Ecosystem."
      />

      <Card className="mb-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setPage(1);
            }}
            placeholder="Todas las acciones"
            options={AUDIT_ACTION_VALUES.map((value) => ({
              value,
              label: AUDIT_ACTION_LABELS[value],
            }))}
            aria-label="Filtrar por accion"
          />
          <Select
            value={entityType}
            onChange={(event) => {
              setEntityType(event.target.value);
              setPage(1);
            }}
            placeholder="Todas las entidades"
            options={[
              { value: 'user', label: 'Personas' },
              { value: 'ticket', label: 'Tickets' },
              { value: 'application', label: 'Aplicaciones' },
            ]}
            aria-label="Filtrar por entidad"
          />
        </div>
      </Card>

      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonList rows={6} />
      ) : (
        <>
          <Table
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(entry) => String(entry.id)}
            empty={
              <EmptyState
                icon={<FileClock className="h-6 w-6" aria-hidden />}
                title="Sin registros de auditoria"
                description="Las acciones relevantes (altas de aplicaciones, cambios en tickets, concesiones de acceso) apareceran aqui."
              />
            }
          />

          {data ? (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}
    </>
  );
}
